import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { getWidgetDefinition } from '../widgetRegistry';

export interface WidgetConfigPanelProps {
  widgetId: string;
  widgetType: string;
  settings: Record<string, unknown>;
  onSave: (settings: Record<string, unknown>) => void;
  onClose: () => void;
}

const WidgetConfigPanel: React.FC<WidgetConfigPanelProps> = ({
  widgetId,
  widgetType,
  settings: initialSettings,
  onSave,
  onClose,
}) => {
  const { colors } = useTheme();
  const def = getWidgetDefinition(widgetType);
  const [settings, setSettings] = useState<Record<string, unknown>>(initialSettings ?? {});

  useEffect(() => {
    const merged = def ? { ...def.defaultSettings, ...initialSettings } : { ...initialSettings };
    setSettings(merged);
  }, [widgetId, widgetType, initialSettings, def]);

  const handleChange = (key: string, value: unknown) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(settings);
    onClose();
  };

  if (!def) {
    return (
      <div className="p-4">
        <p className="text-gray-500">Unknown widget type.</p>
        <button type="button" onClick={onClose} className="mt-2 text-blue-600 hover:underline">
          Close
        </button>
      </div>
    );
  }

  const schema = def.settingsSchema as Record<string, { type?: string; default?: unknown; min?: number; max?: number }> | undefined;
  const hasSchema = schema && Object.keys(schema).length > 0;

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      <h3 className="text-lg font-semibold">{def.name} settings</h3>

      {hasSchema && schema ? (
        <div className="space-y-3">
          {Object.entries(schema).map(([key, field]) => {
            const value = settings[key] ?? field.default;
            if (field.type === 'number') {
              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                  <input
                    type="number"
                    value={Number(value)}
                    min={field.min}
                    max={field.max}
                    onChange={(e) => handleChange(key, e.target.valueAsNumber)}
                    className={`w-full rounded border ${colors.border} ${colors.surface} px-3 py-2 text-sm`}
                  />
                </div>
              );
            }
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                <input
                  type="text"
                  value={String(value ?? '')}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className={`w-full rounded border ${colors.border} ${colors.surface} px-3 py-2 text-sm`}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
            <input
              type="number"
              min={1}
              max={50}
              value={Number(settings.limit ?? 5)}
              onChange={(e) => handleChange('limit', e.target.valueAsNumber)}
              className={`w-full rounded border ${colors.border} ${colors.surface} px-3 py-2 text-sm`}
            />
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Cancel"
          aria-label="Cancel"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        <button
          type="submit"
          className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          title="Save"
          aria-label="Save"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        </button>
      </div>
    </form>
  );
};

export default WidgetConfigPanel;
